<template>
  <article class="flex flex-col gap-2 rounded-md border p-3" :class="appearance.card">
    <header class="flex items-center justify-between gap-3 p-1">
      <div class="flex flex-wrap items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
        <span v-if="authorLabel" class="rounded-full px-2 py-0.5 font-semibold" :class="appearance.badge">{{ authorLabel }}</span>
        <span class="font-medium">{{ note.user?.name ?? 'System' }}</span>
        <span class="text-slate-500 dark:text-slate-400">{{ createdAtLabel }}</span>
      </div>
      <div v-if="!readonly" class="flex flex-row items-end gap-2">
        <button
          class="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          type="button"
          :disabled="deleting"
          @click="$emit('reply', note)"
        >
          {{ replyLabel }}
        </button>
        <button
          class="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
          type="button"
          :disabled="deleting"
          @click="$emit('delete', note)"
        >
          {{ deleting ? deletingLabel : deleteLabel }}
        </button>
      </div>
    </header>

    <blockquote v-if="quotedNote" class="rounded-md border-l-4 border-slate-300 bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
      {{ quotedNote.contents }}
    </blockquote>

    <p class="whitespace-pre-wrap text-sm text-slate-700 py-2 dark:text-slate-200">
      {{ note.contents }}
    </p>

    <div v-if="noteUploads.length > 0" class="flex flex-wrap items-center gap-2">
      <UploadedFile v-for="uploadedFile in noteUploads" :key="uploadedFile.id" :file="uploadedFile" :deletable="false" />
    </div>

    <footer class="text-xs text-slate-500 gap-1 flex justify-between" />
  </article>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UploadedFile from '../../../../../uploadable/resources/js/components/UploadedFile.vue'

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  authorLabel: {
    type: String,
    default: null,
  },
  tone: {
    type: String,
    default: 'default',
  },
  deleting: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['reply', 'delete'])

const { t } = useI18n()

const appearances = {
  default: { card: 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900', badge: '' },
  emerald: {
    card: 'border-emerald-200 border-l-4 border-l-emerald-500 bg-emerald-50/60 dark:border-emerald-900 dark:border-l-emerald-500 dark:bg-emerald-950/30',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  },
  blue: {
    card: 'border-blue-200 border-l-4 border-l-blue-500 bg-blue-50/60 dark:border-blue-900 dark:border-l-blue-500 dark:bg-blue-950/30',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  violet: {
    card: 'border-violet-200 border-l-4 border-l-violet-500 bg-violet-50/60 dark:border-violet-900 dark:border-l-violet-500 dark:bg-violet-950/30',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  },
}
const appearance = computed(() => appearances[props.tone] ?? appearances.default)

const replyLabel = computed(() => t('notes.card.replyButton'))
const deleteLabel = computed(() => t('notes.card.deleteButton'))
const deletingLabel = computed(() => t('notes.card.deletingButton'))
const quotedNote = computed(() => props.note.reply_to_note ?? props.note.replyToNote ?? null)
const noteUploads = computed(() => {
  if (!Array.isArray(props.note.uploads)) {
    return []
  }

  return props.note.uploads
})

const createdAtLabel = computed(() => {
  if (!props.note.created_at) {
    return ''
  }

  const date = new Date(props.note.created_at)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleString()
})
</script>
